import {nanoid} from 'nanoid'
import {ImageElement, Markup} from '../views/LabelBuilder/LabelBuilder.slice'
import storageService from '../services/storage.service'
import logoIcon from '../assets/waterMark.png'
import printService from '../services/print.service'
import {useUserPlan} from "../utils/userPlan.util";

interface LabelStage {
    width: number | string
    height: number | string
    unit: 'mm' | 'in' | 'px'
}

export class WatermarkHelper {
    // Convert regular image → base64
    static convertImageToBase64(src: string): Promise<string> {
        return new Promise((resolve, reject) => {
            const img = new Image()
            img.crossOrigin = 'anonymous' // necessary for local/hosted images
            img.src = src

            img.onload = () => {
                const canvas = document.createElement('canvas')
                canvas.width = img.width
                canvas.height = img.height

                const ctx = canvas.getContext('2d')
                if (!ctx) return reject(new Error('Could not get canvas context'))

                ctx.drawImage(img, 0, 0)
                resolve(canvas.toDataURL('image/png'))
            }

            img.onerror = reject
        })
    }

    // Create watermark element
    static createWatermarkElement(base64: string, pos: string, stage?: LabelStage): ImageElement {
        const width = stage ? Number(stage.width) : Number(localStorage.getItem('canvasWidth'))
        const height = stage ? Number(stage.height) : Number(localStorage.getItem('canvasHeight'))
        const unit = stage ? stage.unit : (localStorage.getItem('canvasUnit') || 'px')

        const MM_TO_PX = 96 / 25.4
        const IN_TO_PX = 96

        let canvasWidthX = width
        let canvasHeightX = height

        if (unit === 'mm') {
            canvasWidthX *= MM_TO_PX
            canvasHeightX *= MM_TO_PX
        } else if (unit === 'in') {
            canvasWidthX *= IN_TO_PX
            canvasHeightX *= IN_TO_PX
        }

        // Scale image down (e.g., 30% of canvas) and center it
        //   const rawWatermarkWidth = canvasWidthX * 0.5 // actual image size
        //   const rawWatermarkHeight = canvasHeightX * 0.5

        const w = 30
        const h = 30

        let x = 0
        let y = 0

        if (pos === 'TR') {
            x = canvasWidthX - w
            y = 0
        } else if (pos === 'BR') {
            x = canvasWidthX - w
            y = canvasHeightX - h
        } else if (pos === 'BL') {
            x = 0
            y = canvasHeightX - h
        } else if (pos === 'TL') {
            x = 0
            y = 0
        } else {
            // Center
            x = (canvasWidthX - w) / 2
            y = (canvasHeightX - h) / 2
        }

        return {
            id: nanoid(),
            type: 'image',
            url: base64,
            width: w,
            height: h,
            scaleX: 1,
            scaleY: 1,
            x,
            y,
            rotation: 0,
            opacity: 0.4
        }
    }

    /* eslint-disable @typescript-eslint/no-unused-vars */

    // TS Overloads (ESLint would complain normally)
    static async applyWatermarks(markups: Markup): Promise<Markup>;
    // eslint-disable-next-line no-dupe-class-members
    static async applyWatermarks(markups: Markup[]): Promise<Markup[]>;

    /* eslint-enable @typescript-eslint/no-unused-vars */

    // eslint-disable-next-line no-dupe-class-members
    static async applyWatermarks(markups: Markup | Markup[]): Promise<Markup | Markup[]> {
        const {isFree, isLabelBuilderUser} = useUserPlan();
        const isFreeUserAndLabelBuilder =
            isLabelBuilderUser &&
            isFree

        if (!isFreeUserAndLabelBuilder) return markups

        const base64 = await WatermarkHelper.convertImageToBase64(logoIcon)

        const applyToOne = (m: Markup): Markup => {
            // Auto-detect if stage exists
            const stage = m.stage

            const wmTL = WatermarkHelper.createWatermarkElement(base64, 'TL', stage)
            const wmTR = WatermarkHelper.createWatermarkElement(base64, 'TR', stage)
            const wmBL = WatermarkHelper.createWatermarkElement(base64, 'BL', stage)
            const wmBR = WatermarkHelper.createWatermarkElement(base64, 'BR', stage)

            return {
                ...m,
                elements: [
                    ...m.elements.filter(el => el.id !== 'watermark'),
                    wmTL,
                    wmTR,
                    wmBL,
                    wmBR
                ]
            }
        }

        return Array.isArray(markups) ? markups.map(applyToOne) : applyToOne(markups)
    }

    static async printWithWM(markupOrMarkups: Markup | Markup[]) {
        if (!markupOrMarkups) return

        const processed = await WatermarkHelper.applyWatermarks(markupOrMarkups)
        printService.printMarkups(processed)
    }
}
